"use client";

/**
 * "Maraq" (interest) card for a residence page and a district trend page:
 * the place's demand index over the last 4 weeks, its trend, and how many
 * people looked, saved, followed or got an alert here, in ranges. Pro;
 * other viewers see the card with the values blurred and the Pro button.
 * Renders nothing when the place has no demand rows at all.
 */
import { useEffect, useRef, useState } from "react";
import { LINK, PRO_BADGE } from "@/lib/ui-classes";
import { Lock } from "lucide-react";
import LocalizedLink from "@/lib/localized-link";
import { useT } from "@/lib/locale-context";
import { useAuth } from "@/lib/auth-context";
import { trackEvent } from "@/lib/tracking";
import type { TranslationKey } from "@/lib/i18n";
import ProCta from "@/components/result/ProCta";
import { DemandBars, TrendBadge } from "@/components/demand/DemandPlacePanel";
import { demandColor, fetchDemandPlace, formatRange, type DemandKind, type DemandPlaceDetail } from "@/lib/demand";

export default function InterestCard({ kind, slug, className = "" }: {
  kind: DemandKind | "auto";
  slug: string;
  className?: string;
}) {
  const t = useT();
  const { user, loading } = useAuth();
  const [data, setData] = useState<DemandPlaceDetail | null | undefined>(undefined);
  const tracked = useRef(false);

  useEffect(() => {
    if (loading) return;
    let live = true;
    fetchDemandPlace(kind, slug, "all")
      .then((d) => { if (live) setData(d); })
      .catch(() => { if (live) setData(null); });
    return () => { live = false; };
  }, [kind, slug, loading, user?.id]);

  useEffect(() => {
    if (!data || tracked.current) return;
    tracked.current = true;
    trackEvent("demand.card.view", { kind: data.kind, locked: data.locked });
  }, [data]);

  if (!data) return null;
  const locked = data.locked;
  const it = data.interest;
  if (!locked && !it) {
    return (
      <section className={`rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 ${className}`}>
        <h2 className="text-base font-bold text-gray-900">{t("demand.card.title")}</h2>
        <p className="mt-1 text-[13px] leading-relaxed text-gray-600">{t("demand.thin.body")}</p>
      </section>
    );
  }
  const blur = locked ? "select-none blur-[5px]" : "";
  const range = (r: Parameters<typeof formatRange>[0]) => formatRange(r, t("demand.range")) ?? t("common.na");
  const stats: [TranslationKey, string][] = [
    ["demand.visitors", locked ? "50-99" : range(it?.sessions)],
    ["demand.card.saves", locked ? "5-9" : range(it?.saves)],
    ...(data.kind === "residence" ? [["demand.card.follows", locked ? "1-4" : range(it?.follows)] as [TranslationKey, string]] : []),
    ["demand.card.alerts", locked ? "10-24" : range(it?.alerts)],
  ];

  return (
    <section className={`rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 ${className}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-base font-bold text-gray-900">{t("demand.card.title")}</h2>
          <p className="text-xs text-gray-500">{t("demand.window.4w")}</p>
        </div>
        {locked && (
          <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${PRO_BADGE}`}>
            <Lock className="h-3 w-3" aria-hidden /> Pro
          </span>
        )}
      </div>

      {/* The numbers on the left and the 12 weeks on the right from sm:
          the chart capped at 384 px left half of a wide card empty
          (owner, 2026-10-22). Phones stack them. */}
      <div className="mt-3 grid gap-x-8 gap-y-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <div className="min-w-0">
          <p className="text-xs font-medium text-gray-500">{t("demand.index")}</p>
          <p className="flex items-baseline gap-1.5">
            {!locked && <span className="h-3 w-3 shrink-0 self-center rounded-full" style={{ backgroundColor: demandColor(it?.index) }} aria-hidden />}
            <span className={`text-3xl font-extrabold tabular-nums tracking-tight text-gray-900 ${blur}`}>
              {locked ? "64" : it?.index ?? t("common.na")}
            </span>
            <span className="text-xs text-gray-500">{t("demand.index.of")}</span>
          </p>
          {!locked && <TrendBadge trend={it?.trend} />}
          <dl className="mt-3 flex flex-wrap gap-x-5 gap-y-2 sm:grid sm:grid-cols-2">
            {stats.map(([k, v]) => (
              <div key={k} className="min-w-0">
                <dt className="text-[11px] text-gray-500">{t(k)}</dt>
                <dd className={`font-bold tabular-nums text-gray-900 ${blur}`}>{v}</dd>
              </div>
            ))}
          </dl>
        </div>
        <div className="min-w-0">
          <DemandBars series={data.series} locked={locked} />
          <p className="mt-1 text-[11px] leading-snug text-gray-500">{t("demand.card.vs")}</p>
        </div>
      </div>

      <div className="mt-3">
        {locked ? (
          <div className="space-y-2">
            <p className="text-[13px] leading-relaxed text-gray-600">{t("demand.card.locked")}</p>
            <div className="max-w-xs" onClick={() => trackEvent("demand.cta.click", { where: "card", kind: data.kind })}>
              <ProCta previewEnded={user?.pro_preview_ended_days_ago != null} />
            </div>
          </div>
        ) : (
          <LocalizedLink
            href="/map/demand"
            onClick={() => trackEvent("demand.card.map", { kind: data.kind })}
            className={`text-[13px] ${LINK}`}
          >
            {t("demand.card.map_link")}
          </LocalizedLink>
        )}
      </div>
    </section>
  );
}
